/**
 * Phone navigation: a floating tab bar for the places people visit most, a
 * search button and a new-issue button. The full sidebar opens from Menu.
 */
import { useUnreadCount } from "@/lib/hooks";
import { openCommand, openCreateIssue, setMobileSidebar } from "@/lib/ui";
import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import { CircleUserRound, Hexagon, House, Inbox, Menu, Plus, Search } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

/** True while a text field has focus, when the on-screen keyboard is likely up. */
function useTyping() {
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const isField = (el: EventTarget | null) => el instanceof HTMLElement && (el.matches("input:not([type=checkbox]):not([type=radio]), textarea, select") || el.isContentEditable);
    const onIn = (e: FocusEvent) => setTyping(isField(e.target));
    const onOut = () => setTyping(false);
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);
  return typing;
}

function Tab({ to, label, icon, active, badge }: { to: string; label: string; icon: ReactNode; active: boolean; badge?: number }) {
  return (
    <Link
      to={to as never}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={cn(
        "tap-none relative flex size-11 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color,transform] duration-200 ease-out-quint active:scale-90 [&_svg]:size-[22px] [&_svg]:stroke-[1.75]",
        active && "bg-accent text-foreground",
      )}
    >
      {icon}
      {!!badge && <span className="absolute top-1.5 right-1.5 size-2.5 rounded-full bg-brand ring-2 ring-popover" />}
    </Link>
  );
}

const round = "tap-none pointer-events-auto flex size-14 shrink-0 items-center justify-center rounded-full transition-transform duration-200 ease-out-quint active:scale-90 [&_svg]:size-6";

export function MobileNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const unread = useUnreadCount();
  const typing = useTyping();
  const at = (p: string) => pathname === p || pathname.startsWith(`${p}/`);
  return (
    <nav
      aria-label="Quick navigation"
      className={cn(
        "pointer-events-none fixed inset-x-0 bottom-0 z-40 flex items-end justify-center gap-2 px-3 pb-[max(12px,env(safe-area-inset-bottom))] transition-[transform,opacity] duration-300 ease-out-quint md:hidden",
        typing && "pointer-events-none translate-y-24 opacity-0",
      )}
    >
      <div className="pointer-events-auto flex h-14 items-center gap-0.5 rounded-full border border-border/80 bg-popover/85 px-1.5 shadow-[0_10px_30px_-10px_rgb(0_0_0/0.35)] backdrop-blur-xl backdrop-saturate-150">
        <Tab to="/" label="Home" icon={<House />} active={pathname === "/"} />
        <Tab to="/inbox" label="Inbox" icon={<Inbox />} active={at("/inbox")} badge={unread} />
        <Tab to="/my-issues" label="My work" icon={<CircleUserRound />} active={at("/my-issues")} />
        <Tab to="/projects" label="Projects" icon={<Hexagon />} active={at("/projects") || at("/project")} />
        <button type="button" onClick={() => setMobileSidebar(true)} aria-label="Menu" className="tap-none flex size-11 items-center justify-center rounded-full text-muted-foreground transition-transform duration-200 ease-out-quint active:scale-90 [&_svg]:size-[22px] [&_svg]:stroke-[1.75]">
          <Menu />
        </button>
      </div>
      <button type="button" onClick={() => openCommand()} aria-label="Search" className={cn(round, "border border-border/80 bg-popover/85 text-foreground shadow-[0_10px_30px_-10px_rgb(0_0_0/0.35)] backdrop-blur-xl")}>
        <Search className="stroke-[1.75]" />
      </button>
      <button
        type="button"
        onClick={() => openCreateIssue()}
        aria-label="New issue"
        className={cn(round, "bg-primary bg-[linear-gradient(180deg,rgb(255_255_255/0.16),transparent)] text-primary-foreground shadow-[inset_0_1px_0_rgb(255_255_255/0.2),0_10px_24px_-8px_var(--primary)]")}
      >
        <Plus className="stroke-[2.25]" />
      </button>
    </nav>
  );
}
